import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon, SearchIcon } from 'lucide-react';
import { Product } from '../types/catalog';
import { searchProducts } from '../utils/quotes';
import { zipCentroids } from '../data/zipCentroids';
interface SearchBarProps {
  productQuery: string;
  onProductQueryChange: (value: string) => void;
  onSelectProduct: (product: Product) => void;
  destinationQuery: string;
  onDestinationChange: (value: string) => void;
  compact: boolean;
}
export function SearchBar({
  productQuery,
  onProductQueryChange,
  onSelectProduct,
  destinationQuery,
  onDestinationChange,
  compact
}: SearchBarProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestions = searchProducts(productQuery);
  useEffect(() => {
    setActiveIndex(0);
  }, [productQuery]);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  function commit(product: Product) {
    onSelectProduct(product);
    setOpen(false);
  }
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(i => (i + 1) % suggestions.length);
      return;
    }
    if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const choice = suggestions[activeIndex] ?? suggestions[0];
      if (choice) commit(choice);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  }
  const fieldHeight = compact ? 'h-12' : 'h-16';
  const textSize = compact ? 'text-base' : 'text-lg md:text-xl';
  return <div ref={containerRef} className="relative w-full" data-id="element-176" data-mp="qafdaa78tc9p">
      <div className={`flex flex-col divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface md:flex-row md:divide-x md:divide-y-0 ${compact ? 'shadow-sm' : 'shadow-md'}`} data-id="element-177" data-mp="cfv3mjp2614t">
        <div className={`relative flex shrink-0 items-center md:flex-1 ${fieldHeight}`} data-id="element-178" data-mp="2hfbcdeaoltq">
          <SearchIcon className="pointer-events-none absolute left-4 h-5 w-5 text-ink-soft" aria-hidden="true" data-id="element-179" data-mp="7j4lzrxluxha" />
          <label htmlFor="product-input" className="sr-only" data-id="element-180" data-mp="3um9gefblc51m">
            What material?
          </label>
          <input id="product-input" type="text" role="combobox" aria-expanded={open && suggestions.length > 0} aria-controls="product-suggestions" aria-autocomplete="list" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} enterKeyHint="search" value={productQuery} onChange={event => {
          onProductQueryChange(event.target.value);
          setOpen(true);
        }} onFocus={() => setOpen(true)} onKeyDown={handleKeyDown} placeholder={'5/8" minus, drain rock, topsoil…'} className={`h-full w-full truncate bg-transparent pl-12 pr-4 ${textSize} font-medium text-ink outline-none placeholder:font-normal placeholder:text-ink-soft focus:ring-0`} data-id="element-181" data-mp="1nvjqlb2bb05m" />
        </div>

        <div className={`relative flex shrink-0 items-center md:w-[19rem] ${fieldHeight}`} data-id="element-182" data-mp="2qpqkkdhfbj5j">
          <MapPinIcon className="pointer-events-none absolute left-4 h-5 w-5 text-ink-soft" aria-hidden="true" data-id="element-183" data-mp="10datizjjwk8s" />
          <label htmlFor="destination-input" className="sr-only" data-id="element-184" data-mp="wcqutrjbx8kj">
            Delivery address
          </label>
          <input id="destination-input" type="text" list="destination-options" autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint="done" value={destinationQuery} onChange={event => onDestinationChange(event.target.value)} placeholder="Deliver to — address, city, or ZIP" className={`h-full w-full truncate bg-transparent pl-12 pr-4 ${compact ? 'text-base' : 'text-base md:text-lg'} text-ink outline-none placeholder:text-ink-soft focus:ring-0`} data-id="element-185" data-mp="12qwhvmzhuhwy" />
          <datalist id="destination-options" data-id="element-186" data-mp="sts4ocnmlwsa">
            {zipCentroids.map(record => <option key={record.zip} value={`${record.city} ${record.zip}`} data-id="element-187" data-mp="3rjih9w9as9w8" />)}
          </datalist>
        </div>
      </div>

      {open && suggestions.length > 0 && <ul id="product-suggestions" role="listbox" className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-lg md:w-[calc(100%-19rem)]" data-id="element-188" data-mp="3oyuaua7fh8zb">
          {suggestions.map((product, index) => <li key={product.id} data-id="element-189" data-mp="3f1zarmvib0ix" data-mpi={index}>
              <button type="button" role="option" aria-selected={index === activeIndex} onMouseEnter={() => setActiveIndex(index)} onClick={() => commit(product)} className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors duration-150 ${index === activeIndex ? 'bg-accent-soft' : 'bg-transparent'}`} data-id="element-190" data-mp="3azb15cyyxpkw">
                <span className="text-sm font-medium text-ink" data-id="element-191" data-mp="2n9gpgyqdy728">{product.name}</span>
                <span className="tnum shrink-0 text-xs uppercase tracking-wide text-ink-soft" data-id="element-192" data-mp="2ia45qmut9g3v">
                  {product.cosSpec ? `${product.cosSpec} · ` : ''}#{product.productNumber}
                </span>
              </button>
            </li>)}
        </ul>}
    </div>;
}